<template>
  <transition name="slide-fade-left">
    <div class="sidecart__line-item" v-if="item">
      <div class="sidecart__line-item__image-wrapper">
        <img :src="imageThumb" :alt="item.product_title" />
      </div>
      <div class="sidecart__line-item__content">
        <div class="sidecart__line-item__header">
          <p class="sidecart__line-item__title">{{ itemName }}</p>
          <button class="sidecart__line-item__remove" v-if="!disableRemove" label="Remove" @click="removeItem" aria-label="Remove item">
            <icon name="close" size="13px"></icon>
          </button>
        </div>
        <div class="sidecart__line-item__option-list">
          <span class="sidecart__line-item__option body-sm">
            Color: {{ staticProductColor }}
          </span>
          <span class="sidecart__line-item__option body-sm" :key="index" v-for="(option, index) in item.options_with_values" v-if="option.value !== 'Default Title'">
            {{option.name}}: {{option.value}}
          </span>
        </div>
        <div class="sidecart__line-item__details">
          <div class="sidecart__line-item__quantity" v-if="!disableQuantity">
            <button class="sidecart__line-item__quantity__change" label="Decrement Count" @click="decrement" aria-label="Decrease count">
              <icon class="fill-black" name="minus"></icon>
            </button>
            <p class="sidecart__line-item__quantity__label body-md">{{ item.quantity }}</p>
            <button class="sidecart__line-item__quantity__change" label="Increment Count" @click="increment" aria-label="Increase count">
              <icon class="fill-black" name="plus"></icon>
            </button>
          </div>
          <p class="sidecart__line-item__price">
            <span class="sidecart__line-item__price--compare-at" v-if="isDiscounted">{{ comparePrice | money }}</span>
            <span v-if="item.line_price > 0" :class="{ ['sidecart__line-item__price--discount']: isDiscounted }">{{ item.line_price | money }}</span>
            <span v-else>
              FREE
            </span>
          </p>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import range from 'lodash/range'
import { mapState } from 'vuex'
import { badge, debounce, imageSize } from '@/scripts/helpers/util.js'
import Icon from '@/scripts/components/basic/Icon.vue'
import QuantitySelector from '@/scripts/components/basic/Quantity.vue'

export default {
  name: 'SidecartLineItem',
  components: {
    Icon,
    QuantitySelector
  },
  props: {
    item: Object,
    line: Number,
    inKit: Boolean,
    _kitId: [Number, String],
    disableQuantity: Boolean,
    disableRemove: Boolean
  },
  data () {
    return {
      updating: false
    }
  },
  computed: {
    ...mapState({
      allProducts: state => state.products.all
    }),
    itemClass () {
      return {
        updating: this.updating
      }
    },
    product () {
      return this.allProducts.find(product => {
        return Boolean(product.variants.find(variant => this.item.id === variant.id))
      })
    },
    variant () {
      return this.product ? this.product.variants.find(variant => variant.id === this.item.id) : null
    },
    hasBaseProduct () {
      return this.item.properties && !!this.item.properties._base_product
    },
    baseProduct () {
      if (this.hasBaseProduct) {
        return this.allProducts.find(product => product.handle === this.item.properties._base_product)
      }

      return null
    },
    itemName () {
      const t = this.item.product_title;
      const productTitle = t.substr(0, t.lastIndexOf('-'));
      if (this.baseProduct) {
        const freq = this.item.properties.shipping_interval_frequency
        const unit = this.item.properties.shipping_interval_unit_type

        return `${productTitle} - ${freq} ${unit} Subscription`
      }

      return productTitle
    },
    staticProductColor() {
      return this.item.product_title.split('-').pop().trim()
    },
    imageThumb () {
      return imageSize(this.item.image, '200x')
    },
    comparePrice () {
      if (this.baseProduct) {
        return this.baseProduct.price * this.item.quantity
      }

      return (this.variant) ? parseInt(this.variant.compare_at_price, 10) * this.item.quantity : this.item.price * this.item.quantity
    },
    isDiscounted () {
      return !isNaN(this.comparePrice) && this.item.price * this.item.quantity < this.comparePrice
    },
    /** @returns {String} */
    productBadge () {
      if (this.variant) {
        return badge(this.product, this.variant, this.isDiscounted)
      }
    },
    subtitlePreText () {
      return window.bvaccel.schema.productTile.subtitle_pretext
    }
  },
  methods: {
    removeItem () {
      this.updating = true
      this.$store.dispatch('cart/removeItems', { key: this.item.key })
        .catch(error => this.$store.dispatch('toast/send', { text: error, type: 'error' }))
        .then(() => this.updating = false)
    },
    increment () {
      this.updateQuantity(this.item.quantity + 1)
    },
    decrement () {
      if (this.item.quantity > 0) {
        this.updateQuantity(this.item.quantity - 1)
      }
    },
    updateQuantity: debounce(function (newQuantity) {
      this.updating = true

      if (newQuantity < 1) {
        this.removeItem()
        return
      }

      this.$store.dispatch('cart/updateItems', { key: this.item.key, quantity: newQuantity })
        .catch(error => this.$store.dispatch('toast/send', { text: error, type: 'error' }))
        .then(() => this.updating = false)
    }, 100)
  },
  created () {
    if (this.hasBaseProduct && !this.baseProduct) {
      this.$store.dispatch('products/addProduct', this.item.properties._base_product)
    }
  }
}
</script>

<style scoped lang="scss">
  .sidecart__line-item {
    display: flex;
    position: relative;
    padding: 20px 0;
    border-bottom: 1px solid $color-lightest-grey;
    &:last-child {
      border-bottom: 0;
    }

    &.updating {
      opacity: 0.5;
      pointer-events: none;

      &.slide-fade-left-leave-active {
        opacity: 0;
      }
    }

    &__image-wrapper {
      width: 100px;
      max-width: 100%;
      height: auto;
      img {
        display: block;
      }
    }

    &__content {
      padding: 10px 0 50px 15px;
      flex-grow: 1;
      position: relative;
    }

    &__header,
    &__details {
      display: flex;
    }

    &-badge {
      position: absolute;
      font-size: 8px;
    }

    &__details {
      position: absolute;
      width: 100%;
      left: 0;
      bottom: 8px;
      margin-top: 12px;
      padding: 0 0 0 15px;

      /deep/ .quantity {
        padding: 8px;
        width: 100%;
        max-width: 120px;
        border: 1px solid $color-light-grey !important;
        border-radius: 2px;
        background-color: $color-white;
      }

      /deep/ .quantity__label {
        width: 100%;
      }

      /deep/ .quantity__change {
        flex: 0 0 auto;

        &:not(:hover) {
          box-shadow: none;
        }
      }
    }

    &__title {
      flex-grow: 1;
      padding-right: 8px;
      margin: 0;
      @include sidecart-line-item-title;
    }

    &__option-list {
      display: flex;
      flex-direction: column;
      margin-top: 10px;
      span {
        color: $color-black;
        font-size: rem(11);
      }
    }

    &__option {
      &:nth-child(1) {
        padding-top: 4px;
      }

      &:last-child {
        padding-bottom: 4px;
      }
    }

    &__remove {
      align-self: flex-start;
      margin-top: 0;
      opacity: 1;
      transition: opacity 200ms ease;
      &:hover {
        opacity: 0.5;
      }
    }

    &__price {
      text-align: right;
      flex-grow: 1;
      line-height: 32px;
      margin: 0;

      &--compare-at {
        color: $color-black;
        text-decoration: line-through;
        font-size: rem(12);
      }

      &--discount {
        color: $color-util-sale;
        margin-left: 5px;
        font-size: rem(12);
      }
      span {
        font-size: rem(12);
      }
    }

    &__quantity {
      display: flex;
      border: none;
      align-items: center;
      &__label {
        width: 27px;
        color: $color-black;
        text-align: center;
        margin: 0 4px;
        font-size: rem(12);
      }

      &__change {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 24px;
        height: 24px;
        &[disabled] {
          opacity: 0.3;
        }
      }
    }

    /deep/ &-quantity-select {
      max-width: 50px;

      .vs__dropdown-menu {
        max-width: 50px;
      }
    }
  }
</style>
